import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import routesfe from '../Config/RoutesFE';
import { pricingPlans, getSavePercentage } from '../Config/PricingPlans';
import configDetails from '../Config/Config';
import { FaSpinner } from 'react-icons/fa';
import PlanCard from '../components copy/PlanCard';
import ContactSalesModal from '../components copy/ContactSalesModal';
import PaymentService from '../services/PaymentService';

const { trackingUrl, GET_TRACKING_TIME } = configDetails;

const PLAN_ORDER = ['basic', 'professional', 'enterprise'];

const UpgradePage = ({ authIdToken }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentPlanName, setCurrentPlanName] = useState('');
  const [currentPlanCode, setCurrentPlanCode] = useState('');
  const [currentPlanType, setCurrentPlanType] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [selectedBillingCycle, setSelectedBillingCycle] = useState(() => {
    return sessionStorage.getItem('noteforge_selected_billing_cycle') || 'MONTHLY';
  });
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [showContactSalesModal, setShowContactSalesModal] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('noteforge_selected_billing_cycle', selectedBillingCycle);
  }, [selectedBillingCycle]);

  useEffect(() => {
    const fetchAndFilter = async () => {
      setLoading(true);
      try {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        const id = storedUser?.cognito_user_id || storedUser?.userSub;
        if (!id || !authIdToken) {
          setFilteredPlans(pricingPlans);
          setLoading(false);
          return;
        }
        const res = await fetch(`${trackingUrl}${GET_TRACKING_TIME}?user_id=${id}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authIdToken
          }
        });
        if (!res.ok) throw new Error('Failed to fetch tracking time');
        const data = await res.json();
        const rawPlanName = (data.subscription_status_detail?.plan_name || '').toLowerCase();
        const planCode = (data.subscription_status_detail?.plan_code || '').toUpperCase();
        const planId = data.subscription_status_detail?.plan_id;
        const subscriptionStatus = (data.subscription_status_detail?.status || '').toLowerCase();
        let planType = (data.subscription_status_detail?.plan_type || '').toUpperCase();
        
        // Normalize planType: ANNUAL → YEARLY to match PricingPlans.js
        if (planType === 'ANNUAL') {
          planType = 'YEARLY';
        }
        // Derive plan type from plan_name, plan_code, or plan_id if plan_type is missing
        if (!planType) {
          if (/annual|yearly/i.test(rawPlanName) || planCode.includes('-ANN')) {
            planType = 'YEARLY';
          } else if (planId) {
            const matchedPlan = pricingPlans.find(p => p.id === planId || p.pricingId === planId);
            planType = matchedPlan?.billingCycle || 'MONTHLY';
          } else {
            planType = 'MONTHLY';
          }
        }
        // Extract base plan name (remove "annual"/"yearly"/"monthly" suffix)
        const planName = rawPlanName.replace(/\s*(annual|yearly|monthly)\s*/gi, '').trim();
        setSubscriptionStatus(subscriptionStatus);
        setCurrentPlanName(planName);
        setCurrentPlanCode(planCode);
        setCurrentPlanType(planType);

        // Filter plans by selected billing cycle (include plans without billingCycle)
        const filtered = pricingPlans.filter(plan => !plan.billingCycle || plan.billingCycle === selectedBillingCycle);
        setFilteredPlans(filtered);
      } catch {
        const filtered = pricingPlans.filter(plan => !plan.billingCycle || plan.billingCycle === selectedBillingCycle);
        setFilteredPlans(filtered);
      }
      setLoading(false);
    };
    fetchAndFilter();
  }, [authIdToken, selectedBillingCycle]);

  const professionalIndex = filteredPlans.findIndex(plan => plan.name.toLowerCase() === 'professional');
  const [hoveredPlan, setHoveredPlan] = useState(null);

  useEffect(() => {
    if (filteredPlans.length > 0 && hoveredPlan === null) {
      const profIdx = filteredPlans.findIndex(p => p.name.toLowerCase() === 'professional');
      setHoveredPlan(profIdx !== -1 ? profIdx : 0);
    }
  }, [filteredPlans]);

  const handleSubscribe = async (planName) => {
    if (planName === 'Free Trial') {
      navigate(routesfe.PLANS);
      return;
    }
    // For Enterprise, don't require billingCycle match since it appears in both
    let selectedPlan;
    if (planName === 'Enterprise') {
      selectedPlan = pricingPlans.find(plan => plan.name === planName);
      if (selectedPlan) {
        setShowContactSalesModal(true);
        return;
      }
    }
    selectedPlan = pricingPlans.find(plan => plan.name === planName && (plan.billingCycle === selectedBillingCycle || !plan.billingCycle));
    if (!selectedPlan) return;
    navigate(routesfe.PAYMENT, {
      state: { selectedPlan, billingCycle: selectedBillingCycle }
    });
  };

  // Calculate savings percentage dynamically for the Professional plan
  const getAnnualSavingsPercentage = () => {
    const monthlyProfessional = pricingPlans.find(p => p.name === 'Professional' && p.billingCycle === 'MONTHLY');
    const yearlyProfessional = pricingPlans.find(p => p.name === 'Professional' && p.billingCycle === 'YEARLY');
    
    if (monthlyProfessional && yearlyProfessional) {
      return getSavePercentage(yearlyProfessional.price, monthlyProfessional.price);
    }
    return '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 d-flex align-items-center justify-content-center">
        <div className="text-center py-5">
          <FaSpinner className="text-primary mb-3" style={{ animation: 'spin 1s linear infinite' }} size={32} />
          <p className="text-muted">Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="py-12 text-center">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-3">
          Upgrade Your Plan
        </h1>
        <p className="text-lg text-gray-500">
          {currentPlanName
            ? `You are currently on the ${currentPlanName.charAt(0).toUpperCase() + currentPlanName.slice(1)} plan`
            : 'Choose a plan that fits your needs'}
        </p>
        {/* Monthly/Yearly Toggle */}
        <div className="mt-8 flex items-center justify-center">
          <div className="inline-flex rounded-full bg-gray-100 p-1 relative">
            {/* Monthly button */}
            <button
              onClick={() => {
                setSelectedBillingCycle('MONTHLY');
                sessionStorage.setItem('noteforge_selected_billing_cycle', 'MONTHLY');
              }}
              className={`px-6 py-2 rounded-full font-semibold transition-all whitespace-nowrap ${
                selectedBillingCycle === 'MONTHLY'
                  ? 'bg-white text-gray-800 shadow-md'
                  : 'bg-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Monthly
            </button>
            
            {/* Annual button with badge */}
            <button
              onClick={() => {
                setSelectedBillingCycle('YEARLY');
                sessionStorage.setItem('noteforge_selected_billing_cycle', 'YEARLY');
              }}
              className={`px-6 py-2 rounded-full font-semibold transition-all whitespace-nowrap flex items-center gap-2 ${
                selectedBillingCycle === 'YEARLY'
                  ? 'bg-white text-gray-800 shadow-md'
                  : 'bg-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Annual
              <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                save {getAnnualSavingsPercentage()}
              </span>
            </button>
          </div>
        </div>
        <div className="absolute top-4 right-8">
          <button
            className="bg-gradient-to-r from-purple-600 via-indigo-600 to-pink-500 text-white px-3 py-2 rounded-2xl shadow-lg font-semibold text-base transition-all duration-200 hover:scale-105 hover:shadow-2xl hover:from-purple-700 hover:via-indigo-700 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-400 min-w-[110px]"
            onClick={() => navigate(routesfe.HOME)}
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4"/></svg>
              Return to Home
            </span>
          </button>
        </div>
      </header>
      <main className="pb-16 px-4">
        <div className="container mx-auto">
          <div className={`grid gap-6 mx-auto justify-center ${filteredPlans.length >= 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 max-w-7xl' : filteredPlans.length >= 3 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl' : filteredPlans.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-2xl' : 'grid-cols-1 max-w-sm'}`}>
            {filteredPlans.map((plan, index) => {
              // Downgrade logic (copied from PlansPage)
              const PLAN_TIERS = { 'free trial': 0, 'basic': 1, 'professional': 2, 'enterprise': 3 };
              const CODE_TO_TIER = { 'FREETRIAL': 0, 'BAS': 1, 'BAS-ANN': 1, 'PRO': 2, 'PRO-ANN': 2, 'ENT': 3 };
              const currentPlanTier = PLAN_TIERS[currentPlanName] || CODE_TO_TIER[currentPlanCode] || 0;
              const planTier = PLAN_TIERS[plan.name.toLowerCase()] || 0;
              const planNameLower = plan.name.toLowerCase();
              const planCodeUpper = (plan.planCode || '').toUpperCase();
              const planBaseCode = planCodeUpper.replace(/-ANN$/, '');
              const currentBaseCode = currentPlanCode.replace(/-ANN$/, '');
              // Free Trial is a one-time offer — disable it if user has ever had any plan (current or past)
              // Also checks subscriptionStatus for expired/completed plans where plan_name may be empty
              const hasSubscriptionHistory = currentPlanName !== '' || subscriptionStatus !== '';
              const isFreeTrialLocked = planNameLower === 'free trial' && hasSubscriptionHistory;
              const isFreeTrialCurrentPlan = planNameLower === 'free trial' && currentPlanName === 'free trial';
              const isCurrentPlan = isFreeTrialCurrentPlan || ((planNameLower === currentPlanName || planCodeUpper === currentPlanCode || planBaseCode === currentBaseCode) && plan.billingCycle === currentPlanType);
              const isDowngrade = currentPlanName && !isCurrentPlan && planTier < currentPlanTier && plan.billingCycle === currentPlanType;
              const isMonthlyWhenOnYearly = currentPlanType === 'YEARLY' && plan.billingCycle === 'MONTHLY';
              const isDisabled = isCurrentPlan || isDowngrade || isMonthlyWhenOnYearly || isFreeTrialLocked;

              
              let buttonLabel = plan.buttonText;
              if (isCurrentPlan) {
                buttonLabel = 'Current Plan';
              } else if (isFreeTrialLocked) {
                buttonLabel = 'Not Available';
              }
              
              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isHighlighted={hoveredPlan === index}
                  onMouseEnter={() => setHoveredPlan(index)}
                  onMouseLeave={() => setHoveredPlan(professionalIndex !== -1 ? professionalIndex : 0)}
                  onSubscribe={handleSubscribe}
                  buttonLabel={buttonLabel}
                  disabled={isDisabled}
                />
              );
            })}
          </div>
        </div>
      </main>
      <ContactSalesModal 
        isOpen={showContactSalesModal} 
        onClose={() => setShowContactSalesModal(false)} 
        planName="Enterprise"
      />
    </div>
  );
};

export default UpgradePage;
